import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Phone, Mail, X, Save, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { AInput, ATextarea, ASelect } from "@/components/admin/AdminInputs";
import { VENDOR_PIPELINE as VENDOR_STAGES, INVESTOR_PIPELINE as INVESTOR_STAGES } from "./crmPipelineShared";

export const LEAD_SOURCES = ["Website", "Marketplace", "Facebook Organic", "Meta Ads", "Add Value Guide", "Deal Analyser",
  "Leaflet", "Rightmove / Old Listing Leaflet", "Referral", "Pattinson", "Estate Agent", "Networking", "LinkedIn", "Other"];
const CONTACT_TYPES = ["Vendor", "Investor", "Both", "Professional / Introducer"];

const ukDate = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "—";
const typeBadge = (t) => t === "Both" ? "bg-brass/20 text-brass" : t === "Investor" ? "bg-blue-100 text-blue-800" : t === "Vendor" ? "bg-amber-100 text-amber-800" : "bg-white/10 text-emerald-100/70";

const EMPTY = { first_name: "", last_name: "", mobile: "", email: "", contact_type: "Vendor", lead_source: "Marketplace",
  address: "", postcode: "", notes: "", assigned_to: "", pipeline_stage: "New Lead", next_follow_up: "" };

function AddLeadSheet({ onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const stages = form.contact_type === "Investor" ? INVESTOR_STAGES : VENDOR_STAGES;

  const save = async () => {
    if (!form.first_name.trim() && !form.last_name.trim()) return toast.error("Enter a name");
    if (!form.mobile.trim() && !form.email.trim()) return toast.error("Enter a mobile or email");
    setBusy(true);
    try {
      const { data } = await api.post("/crm/quick-lead", form);
      toast.success(data.duplicate ? "Existing contact updated" : "Lead added");
      onSaved(data.contact_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save lead"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center" data-testid="add-lead-sheet">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-[#0B241B] border-t sm:border border-white/10 sm:rounded-2xl rounded-t-2xl p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-serif text-white">+ Add Lead</h3>
          <button onClick={onClose} data-testid="add-lead-close" className="p-1"><X className="w-6 h-6 text-emerald-100/70" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <AInput label="First name" value={form.first_name} onChange={set("first_name")} testId="lead-first-name" />
          <AInput label="Last name" value={form.last_name} onChange={set("last_name")} testId="lead-last-name" />
        </div>
        <div className="grid grid-cols-1 gap-3 mt-3">
          <div>
            <label className="block text-xs text-emerald-100/60 mb-1">Mobile</label>
            <input type="tel" inputMode="tel" value={form.mobile} onChange={(e) => set("mobile")(e.target.value)} data-testid="lead-mobile"
              className="w-full bg-[#091E16] border border-white/15 focus:border-brass rounded-lg px-3.5 py-3 text-base text-white outline-none" />
          </div>
          <div>
            <label className="block text-xs text-emerald-100/60 mb-1">Email <span className="text-emerald-100/30">(optional)</span></label>
            <input type="email" inputMode="email" value={form.email} onChange={(e) => set("email")(e.target.value)} data-testid="lead-email"
              className="w-full bg-[#091E16] border border-white/15 focus:border-brass rounded-lg px-3.5 py-3 text-base text-white outline-none" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <ASelect label="Contact type" value={form.contact_type} onChange={set("contact_type")} options={CONTACT_TYPES} testId="lead-type" />
          <ASelect label="Lead source" value={form.lead_source} onChange={set("lead_source")} options={LEAD_SOURCES} testId="lead-source" />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <AInput label="Property address" value={form.address} onChange={set("address")} />
          <AInput label="Postcode" value={form.postcode} onChange={set("postcode")} />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <ASelect label="Pipeline stage" value={form.pipeline_stage} onChange={set("pipeline_stage")} options={stages} testId="lead-stage" />
          <div>
            <label className="block text-xs text-emerald-100/60 mb-1">Next follow-up</label>
            <input type="date" value={form.next_follow_up} onChange={(e) => set("next_follow_up")(e.target.value)} data-testid="lead-followup"
              className="w-full bg-[#091E16] border border-white/15 focus:border-brass rounded-lg px-3 py-3 text-sm text-white outline-none" />
          </div>
        </div>
        <div className="mt-3"><AInput label="Assigned to" value={form.assigned_to} onChange={set("assigned_to")} /></div>
        <div className="mt-3"><ATextarea label="Notes" value={form.notes} onChange={set("notes")} rows={2} /></div>
        <button onClick={save} disabled={busy} data-testid="add-lead-save"
          className="w-full mt-4 flex items-center justify-center gap-2 bg-brass hover:bg-brass-600 disabled:opacity-60 text-emerald-950 font-semibold rounded-full py-3.5 text-base">
          <Save className="w-5 h-5" /> {busy ? "Saving…" : "Save lead"}
        </button>
      </div>
    </div>
  );
}

export default function CrmContactsHub() {
  const navigate = useNavigate();
  const [data, setData] = useState({ total: 0, contacts: [] });
  const [filters, setFilters] = useState({ q: "", contact_type: "", source: "" });
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/crm/unified-contacts", { params }).then((r) => setData(r.data)).catch(() => toast.error("Could not load contacts"));
  }, [filters]);
  useEffect(load, [load]);

  return (
    <CrmShell title="Contacts">
      {showAdd && <AddLeadSheet onClose={() => setShowAdd(false)} onSaved={(id) => navigate(`/crm/contact/contact/${id}`)} />}
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2 text-emerald-100/70 text-sm"><Users className="w-4 h-4 text-brass" /> {data.total} contacts (deduped across investors, vendors & contacts)</div>
        <button onClick={() => setShowAdd(true)} data-testid="add-lead-btn" className="flex items-center gap-1.5 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-5 py-2.5 text-sm shadow-lg"><Plus className="w-4 h-4" /> Add Lead</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-2.5 text-emerald-100/40" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Search name, email, phone" data-testid="hub-search" className="w-full bg-[#0F2E23] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-emerald-100/40" /></div>
        <select value={filters.contact_type} onChange={(e) => setFilters((f) => ({ ...f, contact_type: e.target.value }))} data-testid="hub-type" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"><option value="">All types</option>{CONTACT_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        <select value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))} data-testid="hub-source" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"><option value="">All sources</option>{LEAD_SOURCES.map((s) => <option key={s}>{s}</option>)}</select>
      </div>
      <div className="bg-[#0F2E23] rounded-xl border border-white/10 overflow-x-auto" data-testid="hub-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-emerald-100/50 border-b border-white/10">
            <th className="py-2.5 px-3">Name</th>
            <th className="py-2.5 px-3 hidden sm:table-cell">Contact</th>
            <th className="py-2.5 px-3">Type</th>
            <th className="py-2.5 px-3 hidden md:table-cell">Stage</th>
            <th className="py-2.5 px-3 hidden lg:table-cell">Source</th>
            <th className="py-2.5 px-3 hidden lg:table-cell">Last activity</th>
          </tr></thead>
          <tbody>{data.contacts.map((c) => (
            <tr key={`${c.store}-${c.id}`} onClick={() => navigate(`/crm/contact/${c.store}/${c.id}`)} data-testid={`hub-row-${c.id}`} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
              <td className="py-3 px-3"><div className="text-white font-medium">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</div>{c.linked_records > 1 && <div className="text-[10px] text-brass">{c.linked_records} linked records</div>}</td>
              <td className="py-3 px-3 hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-col gap-0.5">
                  {c.mobile && <a href={`tel:${c.mobile}`} className="flex items-center gap-1 text-emerald-100/70 hover:text-brass text-xs"><Phone className="w-3 h-3" />{c.mobile}</a>}
                  {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-emerald-100/70 hover:text-brass text-xs"><Mail className="w-3 h-3" />{c.email}</a>}
                </div>
              </td>
              <td className="py-3 px-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${typeBadge(c.contact_type)}`}>{c.contact_type}</span></td>
              <td className="py-3 px-3 hidden md:table-cell text-emerald-100/70 text-xs">{c.pipeline_stage || c.stage || "—"}</td>
              <td className="py-3 px-3 hidden lg:table-cell text-emerald-100/60 text-xs">{c.source || "—"}</td>
              <td className="py-3 px-3 hidden lg:table-cell text-emerald-100/60 text-xs">{ukDate(c.last_activity)}</td>
            </tr>))}</tbody>
        </table>
        {data.contacts.length === 0 && <p className="text-emerald-100/50 text-sm py-8 text-center">No contacts match.</p>}
      </div>
    </CrmShell>
  );
}
